import { useCallback, useMemo, useState } from "react";

const DEFAULT_CHARSET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function randomFromCharset(length, charset) {
  let out = "";
  const cryptoObj = globalThis.crypto;

  if (cryptoObj?.getRandomValues) {
    const values = new Uint32Array(length);
    cryptoObj.getRandomValues(values);
    for (let i = 0; i < length; i += 1) {
      out += charset[values[i] % charset.length];
    }
    return out;
  }

  for (let i = 0; i < length; i += 1) {
    out += charset[Math.floor(Math.random() * charset.length)];
  }
  return out;
}

export function useCaptcha(options = {}) {
  const {
    length = 6,
    charset = DEFAULT_CHARSET,
    normalize = (value) => value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase(),
  } = options;

  const [challenge, setChallenge] = useState(() =>
    randomFromCharset(length, charset),
  );
  const [userInput, setUserInput] = useState("");
  const [refreshSeed, setRefreshSeed] = useState(0);

  const normalizedInput = useMemo(
    () => normalize(userInput),
    [userInput, normalize],
  );
  const normalizedChallenge = useMemo(
    () => normalize(challenge),
    [challenge, normalize],
  );

  const isValid =
    normalizedInput.length > 0 && normalizedInput === normalizedChallenge;

  const onInputChange = useCallback(
    (eventOrValue) => {
      const raw =
        typeof eventOrValue === "string"
          ? eventOrValue
          : (eventOrValue?.target?.value ?? "");
      setUserInput(normalize(raw));
    },
    [normalize],
  );

  const reset = useCallback(() => {
    setUserInput("");
  }, []);

  const refresh = useCallback(() => {
    setChallenge(randomFromCharset(length, charset));
    setUserInput("");
    setRefreshSeed((n) => n + 1);
  }, [length, charset]);

  return {
    challenge,
    userInput,
    setUserInput: onInputChange,
    isValid,
    refresh,
    reset,
    refreshSeed,
    inputProps: {
      value: userInput,
      onChange: onInputChange,
      maxLength: length,
      autoComplete: "off",
      spellCheck: false,
      inputMode: "text",
      "aria-label": "Enter captcha code",
    },
  };
}
